"use client";

import React from "react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Plane, IdCard, ShieldCheck, ArrowRight } from "lucide-react";
const services = [{
  id: "readiness",
  destination: "Readiness Audit",
  icon: ShieldCheck,
  description: "A comprehensive assessment of your data, infrastructure, and culture. Discover exactly where your business stands on the AI maturity curve.",
  price: "Fixed",
  unit: "Fee",
  badge: "Assessment",
  link: "/AIReadinessPage"
}, {
  id: "enablement",
  destination: "AI Enablement",
  icon: IdCard,
  description: "Empower your workforce with the tools and knowledge to leverage AI effectively. We provide hands-on training and change management for seamless adoption.",
  price: "Custom",
  unit: "Program",
  badge: "Training",
  link: "/ServicesPage"
}, {
  id: "automation",
  destination: "Workflow Automation",
  icon: Plane,
  description: "Eliminate repetitive tasks and reduce errors through intelligent automation. We build custom AI agents that handle complex business processes autonomously.",
  price: "High",
  unit: "Impact",
  badge: "Efficiency",
  link: "/ServicesPage"
}];
function ServiceCard({
  service,
  index
}: {
  service: typeof services[0];
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const Icon = service.icon;
  return <div ref={ref} data-index={index} className={`flex flex-col lg:flex-row gap-6 p-6 md:p-8 bg-card rounded-lg shadow-sm border border-border hover:shadow-md transition-all duration-500 transform ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`} style={{
    transitionDelay: `${index * 100}ms`
  }}>
      <div className="shrink-0 flex justify-center lg:justify-start">
        <div className="size-16 md:size-20 rounded-lg bg-primary/5 border border-primary/10 text-secondary flex items-center justify-center shadow-inner">
          <Icon className="size-8 md:size-10" strokeWidth={1.5} />
        </div>
      </div>

      <div className="flex-1 space-y-3 text-center lg:text-left">
        <div className="flex flex-col sm:flex-row items-center lg:items-start gap-3 justify-center lg:justify-start">
          <h3 className="text-2xl font-bold font-default text-foreground">
            {service.destination}
          </h3>
          {service.badge && <Badge variant="secondary" className="px-3 py-1 text-xs uppercase tracking-wider font-semibold bg-secondary/10 text-secondary border-none">
              {service.badge}
            </Badge>}
        </div>
        <p className="text-muted-foreground leading-relaxed font-sans max-w-2xl mx-auto lg:mx-0">
          {service.description}
        </p>
      </div>

      <div className="shrink-0 flex flex-col sm:flex-row lg:flex-col items-center justify-between lg:justify-center gap-4 lg:gap-6 w-full lg:w-[200px] border-t lg:border-t-0 lg:border-l border-border pt-6 lg:pt-0 lg:pl-8">
        <div className="text-center lg:text-right w-full">
          <div className="text-sm text-muted-foreground mb-1 font-medium tracking-wide uppercase">Service Model</div>
          <div className="flex items-baseline justify-center lg:justify-end gap-1">
            <span className="text-3xl font-bold font-default text-secondary">{service.price}</span>
            <span className="text-sm font-medium text-muted-foreground">{service.unit}</span>
          </div>
        </div>
        <Button asChild className="w-full sm:w-auto lg:w-full group bg-secondary hover:bg-secondary/90 text-secondary-foreground" size="lg">
          <Link to={service.link}>
            Learn More
            <ArrowRight className="ml-2 size-4 transition-transform group-hover:translate-x-1" />
          </Link>
        </Button>
      </div>
    </div>;
}
export default function OurServicePillars() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll();
  return <section data-section-id="224926" id="our-service-pillars" className="bg-background text-foreground py-20 md:py-32 relative overflow-hidden">
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute top-[-10%] right-[-5%] w-96 h-96 bg-secondary/5 rounded-lg blur-3xl" />
        <div className="absolute bottom-[-10%] left-[-5%] w-96 h-96 bg-primary/5 rounded-lg blur-3xl" />
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <div ref={headerRef} className={`max-w-3xl mx-auto text-center mb-16 transition-all duration-700 transform ${isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <h2 className="text-4xl md:text-5xl font-bold font-default text-foreground mb-6 tracking-tight">
            Our Services
          </h2>
          <p className="text-lg text-foreground font-medium font-sans leading-relaxed mb-4">
            Every engagement begins with a consultation. We assess your unique needs and determine which services best address your challenges and opportunities.
          </p>
          <p className="text-lg text-muted-foreground font-sans leading-relaxed">
            Following our initial consultation, we deploy a tailored approach to help you enable employees, automate repetitive work, and build a scalable AI framework.
          </p>
        </div>

        <div className="flex flex-col gap-6 max-w-5xl mx-auto">
          {services.map((service, index) => <ServiceCard key={service.id} service={service} index={index} />)}
        </div>

        <div className="mt-16 max-w-4xl mx-auto">
          <div className="bg-foreground text-background rounded-lg p-6 md:p-8 flex flex-col md:flex-row items-center justify-between gap-6 shadow-xl relative overflow-hidden">
            <div className="absolute inset-0 bg-[linear-gradient(45deg,transparent_25%,rgba(255,255,255,0.05)_50%,transparent_75%,transparent_100%)] bg-[length:250%_250%,100%_100%] animate-[shimmer_8s_infinite_linear]" />
            <div className="flex items-center gap-4 relative z-10">
              <div className="p-3 bg-background/10 rounded-lg shrink-0">
                <ShieldCheck className="size-8 text-secondary" />
              </div>
              <div>
                <h4 className="text-xl font-bold font-default mb-1">The Outcome Guarantee</h4>
                <p className="text-background/80 font-sans text-sm md:text-base">
                  We focus on measurable ROI. If we don't identify clear productivity gains, we refine our strategy at no extra cost.
                </p>
              </div>
            </div>
            <Button asChild variant="secondary" size="lg" className="shrink-0 relative z-10 w-full md:w-auto bg-secondary hover:bg-secondary/90 text-secondary-foreground border-none">
              <Link to="/ConnectPage">Speak to an Expert</Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}